import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";
import { Dictionary, EntityType, IPatch, EnumConfigMap, UpdateOptions } from "../entities/common";
import { get, post, remove } from "../fetch-interceptor";
import { addOrUpdateOrRemove, IDeletionResult, IEntityStore, partialUpdate, StoreHelper } from "./services/storeHelper";
import { catchApiError, defaultCatch } from "./utils";
import * as NotificationsStore from "./NotificationsStore";
import { Process, ProcessStatus, StagePhase, StagePhaseUpdate } from './process/common';
import { IStageChecklistItemMutation, IProcessStage } from '../entities/Subentities';
import { BaseSubentitiesUpdateAction, ICreationData } from './Subentity';
import { push, RouterAction } from 'react-router-redux';

export interface ProcessesState extends IEntityStore<Process> {
    isLoading: boolean;
    isListLoading: boolean;
    deletionResult?: IDeletionResult[];
}

export interface IProcessStageCreation extends ICreationData {
    checklistItems?: IStageChecklistItemMutation[];
}

export interface IProcessStagePatch extends IPatch<IProcessStage> {
    checklistItems?: IStageChecklistItemMutation[];
}

export const ProcessStatusMap: EnumConfigMap = {
    [ProcessStatus.Active]: { title: "Active", cssClassName: "Active" },
    [ProcessStatus.Inactive]: { title: "Inactive", cssClassName: "Inactive" },
    [ProcessStatus.Draft]: { title: "Draft", cssClassName: "Draft" }
}

interface RequestProcessesAction {
    type: 'REQUEST_PROCESSES';
}

interface ReceiveProcessesAction {
    type: 'RECEIVED_PROCESSES';
    processes: Process[];
}

interface LoadProcessAction {
    type: 'LOAD_PROCESS';
    id: string;
}

interface LoadedProcessAction {
    type: 'LOADED_PROCESS';
    process: Process;
}

interface BulkUpdateProcessesSuccessAction {
    type: 'BULK_UPDATE_PROCESSES_SUCCESS';
    processes: Process[];
}

interface UpdateProcessSuccessAction {
    type: 'UPDATE_PROCESS_SUCCESS';
    process: Process;
}

interface CreateProcessAction {
    type: 'CREATE_PROCESS';
}

interface CreatedProcessAction {
    type: 'CREATED_PROCESS';
    process: Process;
}

interface ReceivedDeleteProcessesResultAction {
    type: 'RECEIVED_REMOVE_PROCESSES_RESULT';
    deletionResult?: IDeletionResult[];
}

interface UpdateProcessStagesAction extends BaseSubentitiesUpdateAction<IProcessStage> {
    type: "UPDATE_PROCESS_STAGES";
}

export type KnownAction = 
    | RequestProcessesAction
    | ReceiveProcessesAction
    | LoadProcessAction
    | LoadedProcessAction
    | BulkUpdateProcessesSuccessAction
    | UpdateProcessSuccessAction
    | CreateProcessAction
    | CreatedProcessAction
    | ReceivedDeleteProcessesResultAction
    | UpdateProcessStagesAction;

const unloadedState: ProcessesState = {
    byId: {},
    allIds: [],
    isLoading: false,
    isListLoading: false,
};

export const actionCreators = {
    requestProcesses: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        get<Process[]>(`api/process`)
            .then(data => {
                dispatch({ type: 'RECEIVED_PROCESSES', processes: data });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'REQUEST_PROCESSES' });  
    },
    loadProcess: (id: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        get<Process>(`api/process/${id}`)
            .then(data => {
                dispatch({ type: 'LOADED_PROCESS', process: data });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'LOAD_PROCESS', id });
    },
    createProcess: (name: string, entityType: EntityType, openOnComplete?: boolean): AppThunkAction<KnownAction | RouterAction> => (dispatch, getState) => {
        post<Process>(`api/process/${entityType}`, { name })
            .then(data => {
                dispatch({ type: 'CREATED_PROCESS', process: data });
                if (openOnComplete) {
                    dispatch(push(`/process/${data.id}`, {}));
                }
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'CREATE_PROCESS' });
    },
    removeProcesses: (ids: string[], redirectBack?: boolean): AppThunkAction<KnownAction | RouterAction> => (dispatch, getState) => {
        post<IDeletionResult[]>(`api/process/bulkDelete`, { ids })
            .then(data => {
                dispatch({ type: "RECEIVED_REMOVE_PROCESSES_RESULT", deletionResult: data });
                if (redirectBack) {
                    dispatch(push('/processes'));
                }
            })
            .catch(defaultCatch(dispatch));
    },
    dismissDeletionResult: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({ type: "RECEIVED_REMOVE_PROCESSES_RESULT" });
    },
    bulkUpdateProcesses: (updates: Dictionary<any>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process[]>(`api/process/bulkUpdate`, updates)
            .then(data => {
                dispatch({ type: 'BULK_UPDATE_PROCESSES_SUCCESS', processes: data });
            })
            .catch(defaultCatch(dispatch));
    },
    updateProcessAttributes: (processId: string, updates: Dictionary<any>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/attributes`, updates)
            .then(data => {
                dispatch({ type: 'UPDATE_PROCESS_SUCCESS', process: data });
            })
            .catch(defaultCatch(dispatch));
    },
    makeProcessDefault: (processId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process[]>(`api/process/${processId}/default`, {})
            .then(data => {
                dispatch({ type: 'BULK_UPDATE_PROCESSES_SUCCESS', processes: data });
            })
            .catch(defaultCatch(dispatch));
    },
    makeOpenedProcessDefault: (processId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/default/opened`, {})
            .then(data => {
                dispatch({ type: 'UPDATE_PROCESS_SUCCESS', process: data });
            })
            .catch(defaultCatch(dispatch));
    },
    cloneProcess: (processId: string): AppThunkAction<KnownAction | RouterAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/clone`, {})
            .then(data => {
                dispatch({ type: 'CREATED_PROCESS', process: data });
                dispatch(push(`/process/${data.id}`, {}));
            })
            .catch(defaultCatch(dispatch));
    },
    updateProcessStagePhases: (processId: string, updates: StagePhaseUpdate[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/stagePhases`, updates)
            .then(data => {
                dispatch({ type: 'LOADED_PROCESS', process: data });
            })
            .catch(defaultCatch(dispatch));
    },
    createProcessPhase: (processId: string, creationInfo: Partial<StagePhase>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/phase`, creationInfo)
            .then(data => {
                dispatch({ type: 'LOADED_PROCESS', process: data });
            })
            .catch(defaultCatch(dispatch));
    },
    deleteProcessPhase: (processId: string, phaseId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        remove<Process>(`api/process/${processId}/phase/${phaseId}`, {})
            .then(data => {
                dispatch({ type: 'LOADED_PROCESS', process: data });
            })
            .catch(defaultCatch(dispatch));
    },
    createProcessStages: (processId: string, stages: IProcessStageCreation[], onSuccess?: () => void, onError?: () => void): AppThunkAction<KnownAction | NotificationsStore.KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/stage`, stages)
            .then(data => {
                dispatch({ type: 'UPDATE_PROCESS_STAGES', entityId: processId, addOrUpdate: data.stages });
                onSuccess?.();
            })
            .catch(reason => {
                onError?.();
                catchApiError(message => dispatch(NotificationsStore.actionCreators.pushNotification(
                    { message, type: NotificationsStore.NotificationType.Error })))(reason);
            });
    },
    updateProcessStages: (processId: string, stages: IProcessStagePatch[], onSuccess?: () => void, onError?: () => void, options?: UpdateOptions): AppThunkAction<KnownAction | NotificationsStore.KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/stage/bulkUpdate`, stages)
            .then(data => {
                dispatch({ type: 'UPDATE_PROCESS_STAGES', entityId: processId, addOrUpdate: data.stages });
                onSuccess?.();
            })
            .catch(reason => {
                onError?.();
                catchApiError(message => dispatch(NotificationsStore.actionCreators.pushNotification(
                    { message, type: NotificationsStore.NotificationType.Error })))(reason);
            });
    },
    dragProcessStagesToPhase: (processId: string, stageIds: string[], phaseId: string, insertBeforeId?: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Process>(`api/process/${processId}/stages/dragToPhase`, { ids: stageIds, insertBeforeId, phaseId })
            .then(data => {
                dispatch({ type: 'UPDATE_PROCESS_STAGES', entityId: processId, addOrUpdate: data.stages });
            })
            .catch(defaultCatch(dispatch));
    },
    removeProcessStages: (processId: string, stageIds: string[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        remove<Process>(`api/process/${processId}/stage`, { ids: stageIds })
            .then(() => {
                dispatch({ type: 'UPDATE_PROCESS_STAGES', entityId: processId, remove: stageIds });
            })
            .catch(defaultCatch(dispatch));
    },
};

export const reducer: Reducer<ProcessesState> = (state: ProcessesState = unloadedState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'REQUEST_PROCESSES':
            return {
                ...state,
                isLoading: true,
                isListLoading: true
            };
        case 'RECEIVED_PROCESSES':
            return {
                ...state,
                ...StoreHelper.create(action.processes),
                isLoading: false,
                isListLoading: false
            };
        case 'CREATE_PROCESS':
            return {
                ...state,
                isLoading: true
            };
        case 'CREATED_PROCESS':
            return {
                ...state,
                ...StoreHelper.addOrUpdate(state, action.process),
                isLoading: false
            };
        case 'LOAD_PROCESS':
            return {
                ...state,
                activeEntityId: action.id,
                activeEntity: state.activeEntity && state.activeEntity.id === action.id ? state.activeEntity : undefined,
                isLoading: true
            };
        case 'LOADED_PROCESS': {
            const process = orderProcessStages(action.process);
            return {
                ...state,
                ...StoreHelper.addOrUpdate(state, process),
                activeEntity: state.activeEntityId === process.id ? process : state.activeEntity,
                isLoading: false
            };
        }
        case 'BULK_UPDATE_PROCESSES_SUCCESS':
            return {
                ...state,
                ...StoreHelper.union(state, action.processes),
                isLoading: false
            };
        case 'UPDATE_PROCESS_SUCCESS': {
            const process = orderProcessStages(action.process);
            return {
                ...state,
                ...StoreHelper.addOrUpdate(state, process),
                isLoading: false
            };
        }
        case 'RECEIVED_REMOVE_PROCESSES_RESULT': {
            let newState = { ...state };
            action.deletionResult?.forEach(result => {
                if (result.isDeleted && newState.byId[result.id]) {
                    newState = { ...newState, ...StoreHelper.remove(newState, result.id) };
                }
            });
            return {
                ...newState,
                isLoading: false,
                deletionResult: action.deletionResult
            };
        }
        case 'UPDATE_PROCESS_STAGES': {
            return StoreHelper.applyHandler(state, action.entityId, (process: Process) => {
                const stages = addOrUpdateOrRemove(process.stages, action.addOrUpdate, action.remove);
                const processWithOrderedStages = orderProcessStages({ ...process, stages });
                if (action.remove?.length) {
                    recalculateStagesOrder(processWithOrderedStages);
                }
                return processWithOrderedStages;
            });
        }
        default:
            return state;
    }
};

function recalculateStagesOrder(process: Process) {
    process.stages.forEach((stage, index) => stage.attributes = { ...stage.attributes, Order: index + 1 });
}

function orderProcessStages(process: Process): Process {
    const orderedStages = [...process.stages].sort((a, b) => a.attributes.Order - b.attributes.Order);
    return partialUpdate(process, { stages: orderedStages });
}
